import React, { useState } from "react";
import { CalendarRange, RefreshCw, BookmarkPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PowerballBall } from "./PowerballBall";
import { toast } from "sonner";

export function WeekPicks({ strategy, strategyLabel }) {
  const [picks, setPicks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const isAI = ["ai_oracle", "claude_oracle", "council"].includes(strategy);

  const generate = async () => {
    setLoading(true); setSaved(false);
    try {
      const { data } = await api.post("/predict/week", { strategy });
      setPicks(data.picks);
    } catch (e) {
      toast.error(e.response?.data?.detail || "The Oracle could not see the week ahead");
    } finally {
      setLoading(false);
    }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const body = { picks: picks.map((p) => ({ whites: p.whites, powerball: p.powerball, strategy: p.strategy, confidence: p.confidence, reasoning: p.reasoning, target_draw_date: p.target_draw_date, label: `${p.target_weekday} ${p.target_draw_date}` })) };
      const { data } = await api.post("/picks/bulk", body);
      setSaved(true);
      toast.success(`${data.saved} picks saved — one for each draw this week`);
      window.dispatchEvent(new Event("pb:picks-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="week-picks" className="mt-10 max-w-5xl mx-auto">
      {!picks ? (
        <div className="text-center">
          <Button data-testid="week-picks-btn" onClick={generate} disabled={loading} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps border border-white/10 hover:border-oracle/40 px-6 py-5">
            {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Casting the week{isAI ? " (three oracle calls)" : ""}…</> : <><CalendarRange className="w-4 h-4 mr-2" strokeWidth={1.5} /> Cast a full week — Mon · Wed · Sat</>}
          </Button>
          <p className="font-body text-[11px] text-parchment/40 mt-2">One {strategyLabel} pick for each of the next three drawings, saved together in one tap.</p>
        </div>
      ) : (
        <div className="bg-surface border border-oracle/20 rounded-xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="label-caps text-oracle flex items-center gap-2"><CalendarRange className="w-3 h-3" strokeWidth={1.5} /> Your week · {strategyLabel}</div>
              <p className="font-body text-xs text-parchment/50 mt-1">Each pick is aimed at its own drawing and will be scored against it.</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="week-save-all-btn" onClick={saveAll} disabled={saving || saved} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps">
                {saved ? <><Check className="w-4 h-4 mr-2" /> Saved</> : saving ? "Saving…" : <><BookmarkPlus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save all {picks.length}</>}
              </Button>
              <Button data-testid="week-recast-btn" onClick={generate} disabled={loading} variant="ghost" className="text-parchment hover:text-oracle hover:bg-white/5 label-caps">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} /> Re-cast
              </Button>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            {picks.map((p) => (
              <div key={p.target_draw_date} data-testid={`week-pick-${p.target_draw_date}`} className="bg-black/30 border border-white/5 rounded-lg p-4">
                <div className="flex items-baseline justify-between">
                  <div className="label-caps text-oracle">{p.target_weekday}</div>
                  <div className="font-body text-[11px] text-parchment/50">{p.target_label}</div>
                </div>
                <div className="mt-3 flex items-center gap-1 flex-nowrap">
                  {p.whites.map((n) => <PowerballBall key={n} n={n} size="sm" />)}
                  <span className="text-parchment/30">·</span>
                  <PowerballBall n={p.powerball} red size="sm" />
                </div>
                <div className="mt-3 font-body text-[11px] text-parchment/45">Confidence {(p.confidence * 100).toFixed(0)}%{p.strategy?.endsWith("_fallback") ? " · fallback" : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
